import type { PanoramaFactory } from '@wix/panorama-client-node';
import VError from 'verror';
import { isType } from 'variant';
import {
  createGlobalConfig,
  panoramaClientFactory,
  PanoramaPlatform,
  nodeReporter,
} from '@wix/panorama-client-node';
import { readUserConfig } from '@wix/cli-user-config';
import { getTestOverrides } from '@wix/cli-test-overrides';
import type { ErrorReporter } from '@wix/cli-error-reporting';
import { createEnv } from '@wix/cli-env';
import { CliSystemErrorCode } from '@wix/cli-error';
import { extractHttpError } from '@wix/cli-http-client';

// https://dev.wix.com/docs/fed-guild/articles/infra/panorama/client/for-standalone-projects#factory-initialization
const globalConfig = createGlobalConfig();

const overrides = getTestOverrides();
if (overrides.panoramaReporterUrl) {
  globalConfig.setReporter(
    nodeReporter(overrides.panoramaReporterUrl, { silent: true })
  );
}

export interface Panorama extends PanoramaFactory {
  setDefaultClientData: (data: Record<string, string>) => void;
}

function createPanoramaWrapper(factory: PanoramaFactory): Panorama {
  let defaultData: Record<string, string> = {};

  return {
    withGlobalConfig: (...args) => factory.withGlobalConfig(...args),
    withReporter: (...args) => factory.withReporter(...args),
    setMuted: (...args) => factory.setMuted(...args),
    setDefaultClientData(data: Record<string, string>) {
      defaultData = { ...defaultData, ...data };
    },
    client(options) {
      return factory.client({
        ...options,
        data: { ...defaultData, ...options?.data },
      });
    },
  };
}

interface PanoramaOptions {
  version: string;
  artifact: {
    groupId: string;
    artifactId: string;
  };
  errorReporter?: ErrorReporter;
  userId?: string;
  cliSessionId: string;
}

export async function createPanorama(options: PanoramaOptions) {
  const { version, artifact, userId, errorReporter, cliSessionId } = options;

  const userConfig = await readUserConfig();
  const env = createEnv();

  const factory = panoramaClientFactory({
    baseParams: {
      platform: PanoramaPlatform.Standalone,
      fullArtifactId: `${artifact.groupId}.${artifact.artifactId}`,
      componentId: 'app', // This property is mandatory, there is validation on server side
      artifactVersion: version,
      uuid: userId,
    },
    reporterOptions: {
      silent: true,
    },
    data: {
      cliTrackingId: env.cliTrackingId,
      cliEnvName: env.cliEnvName,
      cliSessionId,
      ...env.remoteLogContext,
    },
  })
    .withGlobalConfig(globalConfig)
    .setMuted(!userConfig.telemetry);

  const panorama = createPanoramaWrapper(factory);

  if (errorReporter) {
    errorReporter.on('errorReported', (cliError) => {
      const panoramaClient = panorama.client({
        baseParams: {
          componentId: 'error-reporter',
        },
      });

      const errorMonitor = panoramaClient.errorMonitor();

      const error =
        isType(cliError.cliCode, CliSystemErrorCode.UnknownFailure) &&
        cliError.cliCode.error instanceof Error
          ? cliError.cliCode.error
          : cliError;

      const errorToReport = new Error(error.message);
      errorToReport.name = error.name;
      errorToReport.stack = VError.fullStack(error);

      const httpError = extractHttpError(cliError);
      if (httpError) {
        // panorama by default traverses the error and tries to extract request id
        errorToReport.cause = httpError;
      }

      errorMonitor.reportError(errorToReport, {
        // panorama by default uses constructor.name for errorName
        errorName: cliError.name,
      });
    });
  }

  return panorama;
}
